import { useAthleteZones } from './useAthleteZones';
import { useEffect, useState } from 'react';
import { athleteSettingsApi } from '../services/athleteSettingsApi';
import { calendar, home } from '../services/api';
import {
  collectCalendarEvents,
  fatigueLevelFromTsb,
  formatGoalRaceLabel,
  getNextGoalRace,
  type FatigueLevel,
} from '../utils/athleteHeaderMetrics';

export interface AthleteMetrics {
  zoneSettings: Record<string, unknown> | null;
  weight: number | null;
  ftp: number | null;
  maxHr: number | null;
  ltHr: number | null;
  /** Lactate-threshold speed (m/s) from speed_zones. */
  thresholdSpeedMps: number | null;
  wPerKg: number | null;
  /** Chronic Training Load from home `today_trends`. */
  ctl: number | null;
  /** Training Stress Balance from home `today_trends`. */
  tsb: number | null;
  fatigue: FatigueLevel | null;
  goalRace: string | null;
  loading: boolean;
}

function futureISODate(monthsAhead: number): string {
  const d = new Date();
  d.setMonth(d.getMonth() + monthsAhead);
  return d.toISOString().slice(0, 10);
}

/** Loads athlete settings, form (TSB), and next A-race for calendar header display. */
export function useAthleteMetrics(userId: number | null, refreshToken = 0): AthleteMetrics {
  const { zoneSettings: sharedZoneSettings } = useAthleteZones(userId);
  const [weight, setWeight] = useState<number | null>(null);
  const [ftp, setFtp] = useState<number | null>(null);
  const [maxHr, setMaxHr] = useState<number | null>(null);
  const [ltHr, setLtHr] = useState<number | null>(null);
  const [thresholdSpeedMps, setThresholdSpeedMps] = useState<number | null>(null);
  const [ctl, setCtl] = useState<number | null>(null);
  const [tsb, setTsb] = useState<number | null>(null);
  const [fatigue, setFatigue] = useState<FatigueLevel | null>(null);
  const [goalRace, setGoalRace] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!userId) {
      setWeight(null);
      setFtp(null);
      setMaxHr(null);
      setLtHr(null);
      setThresholdSpeedMps(null);
      setCtl(null);
      setTsb(null);
      setFatigue(null);
      setGoalRace(null);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const today = new Date().toISOString().slice(0, 10);

    Promise.all([
      athleteSettingsApi.getAthleteDetails(userId),
      athleteSettingsApi.getAthleteZones(userId).catch(() => null),
      home.getHomeData(userId).catch(() => null),
      calendar.getCalendarData(userId, today, futureISODate(18)).catch(() => null),
    ])
      .then(([detailsRes, zonesRes, homeRes, calendarRes]) => {
        if (cancelled) return;

        const setting = detailsRes.data.data.details?.setting as {
          weight?: number | string;
          ftp?: number | string;
          max_hr?: number | string;
          lt_hr?: number | string;
        } | null | undefined;

        const details = detailsRes.data.data.details as { latest_weight?: number | string | null } | undefined;
        const rawLatestWeight = details?.latest_weight;
        const parsedLatestWeight =
          rawLatestWeight != null && rawLatestWeight !== '' ? Number(rawLatestWeight) : null;

        const rawWeight = setting?.weight;
        const parsedSettingWeight =
          rawWeight != null && rawWeight !== '' ? Number(rawWeight) : null;

        const effectiveWeight =
          parsedLatestWeight != null && !Number.isNaN(parsedLatestWeight) && parsedLatestWeight > 0
            ? parsedLatestWeight
            : parsedSettingWeight != null && !Number.isNaN(parsedSettingWeight) && parsedSettingWeight > 0
              ? parsedSettingWeight
              : null;

        let parsedFtp =
          setting?.ftp != null && setting.ftp !== '' ? Number(setting.ftp) : null;

        let parsedMaxHr =
          setting?.max_hr != null && setting.max_hr !== '' ? Number(setting.max_hr) : null;

        let parsedLtHr =
          setting?.lt_hr != null && setting.lt_hr !== '' ? Number(setting.lt_hr) : null;

        let parsedThresholdSpeed: number | null = null;

        if (zonesRes) {
          const zones = zonesRes.data.data.zones as {
            ftp?: number | string;
            max_hr?: number | string;
            lt_hr?: number | string;
            heart_rate_zones?: { threshold?: number | string };
            speed_zones?: {
              threshold?: number | string;
              pace_seconds_per_km?: number | string;
            };
          } | undefined;
          if (zones?.ftp != null && zones.ftp !== '') {
            parsedFtp = Number(zones.ftp);
          }
          if (zones?.max_hr != null && zones.max_hr !== '') {
            parsedMaxHr = Number(zones.max_hr);
          }
          if (zones?.lt_hr != null && zones.lt_hr !== '') {
            parsedLtHr = Number(zones.lt_hr);
          } else if (zones?.heart_rate_zones?.threshold != null && zones.heart_rate_zones.threshold !== '') {
            parsedLtHr = Number(zones.heart_rate_zones.threshold);
          }
          const sd = zones?.speed_zones;
          if (sd?.threshold != null && sd.threshold !== '') {
            parsedThresholdSpeed = Number(sd.threshold);
          } else if (sd?.pace_seconds_per_km != null && Number(sd.pace_seconds_per_km) > 0) {
            parsedThresholdSpeed = 1000 / Number(sd.pace_seconds_per_km);
          }
        }

        setWeight(effectiveWeight);
        setFtp(parsedFtp != null && !Number.isNaN(parsedFtp) && parsedFtp > 0 ? parsedFtp : null);
        setMaxHr(parsedMaxHr != null && !Number.isNaN(parsedMaxHr) && parsedMaxHr > 0 ? parsedMaxHr : null);
        setLtHr(parsedLtHr != null && !Number.isNaN(parsedLtHr) && parsedLtHr > 0 ? parsedLtHr : null);
        setThresholdSpeedMps(
          parsedThresholdSpeed != null && !Number.isNaN(parsedThresholdSpeed) && parsedThresholdSpeed > 0
            ? parsedThresholdSpeed
            : null,
        );

        const trends = homeRes?.data.data.today_trends;
        const rawCtl = trends?.ctl != null ? Number(trends.ctl) : null;
        const rawTsb = trends?.tsb != null ? Number(trends.tsb) : null;
        setCtl(rawCtl != null && !Number.isNaN(rawCtl) ? rawCtl : null);
        setTsb(rawTsb != null && !Number.isNaN(rawTsb) ? rawTsb : null);
        setFatigue(fatigueLevelFromTsb(rawTsb));

        const days = calendarRes?.data.data ?? [];
        const events = collectCalendarEvents(days);
        const nextRace = getNextGoalRace(events, today);
        setGoalRace(formatGoalRaceLabel(nextRace));
      })
      .catch(() => {
        if (!cancelled) {
          setWeight(null);
          setFtp(null);
          setMaxHr(null);
          setLtHr(null);
          setThresholdSpeedMps(null);
          setCtl(null);
          setTsb(null);
          setFatigue(null);
          setGoalRace(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [userId, refreshToken]);

  const positive = (value: unknown) => Number.isFinite(Number(value)) && Number(value) > 0 ? Number(value) : null;
  const power = sharedZoneSettings?.power_zones as { threshold?: unknown } | undefined;
  const heart = sharedZoneSettings?.heart_rate_zones as { threshold?: unknown } | undefined;
  const speed = sharedZoneSettings?.speed_zones as { threshold?: unknown; pace_seconds_per_km?: unknown } | undefined;
  const currentFtp = positive(sharedZoneSettings?.ftp) ?? positive(power?.threshold) ?? ftp;
  const currentMaxHr = positive(sharedZoneSettings?.max_hr) ?? maxHr;
  const currentLtHr = positive(sharedZoneSettings?.lt_hr) ?? positive(heart?.threshold) ?? ltHr;
  const currentSpeed = positive(speed?.threshold) ?? (positive(speed?.pace_seconds_per_km) ? 1000 / Number(speed?.pace_seconds_per_km) : thresholdSpeedMps);
  const wPerKg =
    currentFtp != null && weight != null && weight > 0
      ? Math.round((currentFtp / weight) * 100) / 100
      : null;

  return { zoneSettings: sharedZoneSettings, weight, ftp: currentFtp, maxHr: currentMaxHr, ltHr: currentLtHr, thresholdSpeedMps: currentSpeed, wPerKg, ctl, tsb, fatigue, goalRace, loading };
}
